import styled from "@emotion/styled";
import { PureComponent } from "react";

import { Error, Info } from "@livechat/design-system-icons";
import { DesignToken, Icon, SpacingToken, Text, Tooltip } from "@livechat/design-system-react-components";

import { ExternalLink } from "components/external-link";
import { HelpCenter } from "constants/help-center";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { TicketDeliveryStatus as TicketDeliveryStatusEnum } from "interfaces/entities/ticket";
import { withVar } from "themes/helpers";
import { capitalizeFirstLetter } from "utils/string";

interface IProps {
  status: TicketDeliveryStatusEnum;
}

const StyledStatus = styled(Text)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: ${withVar(SpacingToken.Spacing1)};
`;

const StyledStatusDefault = styled(StyledStatus)`
  color: var(${DesignToken.ContentBasicSecondary});
`;

const StyledStatusError = styled(StyledStatus)`
  color: var(${DesignToken.ActionNegativeDefault});
`;

const StyledIcon = styled(Icon)`
  margin-right: var(${SpacingToken.Spacing1});
`;

const StyledExternalLink = styled(ExternalLink)`
  color: ${withVar(DesignToken.ContentInvertPrimary)};
`;

const errorStatuses: Set<TicketDeliveryStatusEnum> = new Set([
  TicketDeliveryStatusEnum.Bounced,
  TicketDeliveryStatusEnum.Rejected,
  TicketDeliveryStatusEnum.Spam,
  TicketDeliveryStatusEnum.SizeExceeded,
  TicketDeliveryStatusEnum.Failed,
  TicketDeliveryStatusEnum.Unsubscribed,
]);

const statusesMap = {
  [TicketDeliveryStatusEnum.Bounced]: "Delivery issue",
  [TicketDeliveryStatusEnum.Rejected]: "Delivery issue",
  [TicketDeliveryStatusEnum.Spam]: "Delivery issue",
  [TicketDeliveryStatusEnum.SizeExceeded]: "Failed",
  [TicketDeliveryStatusEnum.Opened]: "Read",
  [TicketDeliveryStatusEnum.Unsent]: "Not sent",
  [TicketDeliveryStatusEnum.Unsubscribed]: "Opt-out",
};
// @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
const formatStatus = (status: TicketDeliveryStatusEnum): string => statusesMap[status] || capitalizeFirstLetter(status);

const tooltipsMap = {
  [TicketDeliveryStatusEnum.Bounced]: (
    <>
      <strong>Hard bounce</strong> – The server was unable to deliver your message.{" "}
      <StyledExternalLink
        target="_blank"
        href={HelpCenter.EMAIL_DELIVERY_TRACKING}
        onClick={() => trackEngagement("Spam event: Learn more clicked", EventPlace.Tickets)}
      >
        Learn more
      </StyledExternalLink>
    </>
  ),
  [TicketDeliveryStatusEnum.Rejected]: (
    <>
      <strong>Message rejected</strong> – The server was unable to deliver your message.{" "}
      <StyledExternalLink
        target="_blank"
        href={HelpCenter.EMAIL_DELIVERY_TRACKING}
        onClick={() => trackEngagement("Spam event: Learn more clicked", EventPlace.Tickets)}
      >
        Learn more
      </StyledExternalLink>
    </>
  ),
  [TicketDeliveryStatusEnum.Spam]: (
    <>
      <strong>Spam complaint</strong> – The message was blocked by the user or classified as spam.{" "}
      <StyledExternalLink
        target="_blank"
        href={HelpCenter.EMAIL_DELIVERY_TRACKING}
        onClick={() => trackEngagement("Spam event: Learn more clicked", EventPlace.Tickets)}
      >
        Learn more
      </StyledExternalLink>
    </>
  ),
  [TicketDeliveryStatusEnum.SizeExceeded]: <>Message exceeds 10 MB limit</>,
  [TicketDeliveryStatusEnum.Failed]: <>An unknown error has occured.</>,
  [TicketDeliveryStatusEnum.Unsubscribed]: (
    <>
      <strong>Opt-out</strong> – The recipient has opted out of receiving further messages.{" "}
      <StyledExternalLink
        target="_blank"
        href={HelpCenter.EMAIL_DELIVERY_TRACKING}
        onClick={() => trackEngagement("Spam event: Learn more clicked", EventPlace.Tickets)}
      >
        Learn more
      </StyledExternalLink>
    </>
  ),
};
// @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
const getTooltipMessage = (status: TicketDeliveryStatusEnum): string => tooltipsMap[status] || "";

export class TicketDeliveryStatus extends PureComponent<IProps> {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    const { status } = this.props;
    const isError = errorStatuses.has(status);

    return (
      <>
        {isError && (
          <StyledStatusError size="sm">
            <Tooltip kind="invert" placement="left" triggerRenderer={<StyledIcon source={Error} size="small" />}>
              {getTooltipMessage(status)}
            </Tooltip>
            {formatStatus(status)}
          </StyledStatusError>
        )}
        {!isError && (
          <StyledStatusDefault size="sm">
            <Icon source={Info} size="small"></Icon>
            {formatStatus(status)}
          </StyledStatusDefault>
        )}
      </>
    );
  }
}
